/**
 * TaxEmailRequestsSection (TAX-PENDING-EMAIL-001 / TAX-EMAIL-RECEIVED-001)
 * Tax Collector Email Delivery — Fax Delivery layout: summary line + one card per jurisdiction request with
 * Received (response upload, required) / Resend (hidden once received), recipient, sent, by, delivery status.
 */
import React, { useEffect, useState } from 'react';
import { Mail, Loader2, Eye } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../ui/alert-dialog';
import TaxEmailRequestCard from './TaxEmailRequestCard';
import TaxEmailHistory from './TaxEmailHistory';
import { aggregateTaxEmailStatus, taxEmailTooltip, splitCurrentAndHistory } from '../../lib/taxEmailStatus';

const API = process.env.REACT_APP_BACKEND_URL;

const SUMMARY_CLS = { sent: 'text-slate-500', partial: 'text-slate-600', received: 'text-blue-600', reviewed: 'text-green-600' };

const RESEND_ROLES = ['admin', 'tax_search_team_leader', 'tax_processor', 'processor', 'team_leader', 'qc_specialist'];

export default function TaxEmailRequestsSection({ orderData, currentUser, onUpdate, showPreview = false }) {
  const [confirm, setConfirm] = useState(null);
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [localRequests, setLocalRequests] = useState(null);
  const [localStatus, setLocalStatus] = useState(undefined);
  const allRequests = localRequests || orderData?.tax_email_requests || [];
  const { current: requests, history } = splitCurrentAndHistory(allRequests);
  const agg = aggregateTaxEmailStatus(localStatus !== undefined ? localStatus : orderData?.tax_email_status, allRequests);
  const canResend = RESEND_ROLES.includes((currentUser?.role || '').toLowerCase());
  useEffect(() => { setLocalRequests(null); setLocalStatus(undefined); }, [orderData?.tax_email_requests, orderData?.tax_email_status]);
  if (!requests.length && !(showPreview && canResend)) return null;

  const loadPreview = async () => {
    setPreviewOpen(true);
    setPreview(null);
    try {
      const r = await fetch(`${API}/api/orders/${orderData.id}/tax-email/preview`, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || 'Preview failed');
      setPreview(data);
    } catch (e) {
      toast.error(e.message);
      setPreviewOpen(false);
    }
  };

  const resend = async (rec) => {
    setBusy(true);
    try {
      const r = await fetch(`${API}/api/orders/${orderData.id}/tax-email/resend`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token')}` },
        body: JSON.stringify({ request_id: rec.id }),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || 'Resend failed');
      if (data.status === 'queued') toast.success(`Tax information request re-sent to ${data.record?.recipient}`);
      else if (data.status === 'no_recipient') toast.error(`Still no Tax Collector email configured for ${rec.jurisdiction_name}`);
      else toast.warning(data.record?.error || `Email not sent (${data.status})`);
      onUpdate && onUpdate();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy(false);
      setConfirm(null);
    }
  };

  return (
    <div className="rounded-lg border border-slate-200 bg-white" data-testid="tax-email-requests-section">
      <div className="px-4 py-2.5 border-b border-slate-100 flex items-center gap-2">
        <Mail className="w-4 h-4 text-indigo-600" />
        <h4 className="text-sm font-semibold text-slate-800">Tax Collector Email Delivery</h4>
        {showPreview && canResend && (
          <Button size="sm" variant="ghost" className="h-7 text-xs gap-1 ml-2" onClick={loadPreview} data-testid="tax-email-preview-btn">
            <Eye className="w-3 h-3" /> Preview email
          </Button>
        )}
        <span className="text-xs text-slate-400 ml-auto">{allRequests.length ? `${allRequests.length} record${allRequests.length > 1 ? 's' : ''}` : 'No requests yet'}</span>
      </div>
      {agg.state && (
        <p className={`px-4 pt-2.5 text-xs font-medium ${SUMMARY_CLS[agg.state] || 'text-slate-500'}`} data-testid={`tax-email-summary-${agg.state}`}>
          {taxEmailTooltip(agg)}
        </p>
      )}
      {!requests.length && (
        <p className="px-4 py-3 text-xs text-slate-500" data-testid="tax-email-empty">
          Select <span className="font-medium">Pending → Email</span> in Workflow Actions to send the standardized Tax Information Request to this order's jurisdiction Tax Collector email.
        </p>
      )}
      <div className="p-3 space-y-2">
        {requests.map((rec) => (
          <TaxEmailRequestCard key={rec.id} rec={rec} orderId={orderData.id} canAction={canResend} busy={busy}
            onResend={setConfirm}
            onReceived={(data) => {
              if (data.tax_email_requests) setLocalRequests(data.tax_email_requests);
              setLocalStatus(data.tax_email_status ?? null);
              onUpdate && onUpdate();
            }} />
        ))}
        {history.length > 0 && <TaxEmailHistory history={history} />}
      </div>

      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="tax-email-preview-dialog">
          <DialogHeader>
            <DialogTitle>Standardized Tax Information Request — preview (nothing is sent)</DialogTitle>
          </DialogHeader>
          {!preview ? <div className="flex items-center gap-2 text-sm text-slate-500 py-6"><Loader2 className="w-4 h-4 animate-spin" /> Rendering…</div> : (
            <div className="space-y-4 text-sm">
              <div className="grid grid-cols-[110px_1fr] gap-y-1 text-xs bg-slate-50 border border-slate-200 rounded-md p-3">
                <span className="text-slate-500">From</span><span data-testid="tax-email-preview-from">{preview.from}</span>
                <span className="text-slate-500">Reply-To</span><span>{preview.reply_to || <em className="text-rose-600">not configured</em>}</span>
                <span className="text-slate-500">CC</span><span>{preview.cc?.length ? preview.cc.join(', ') : '—'}</span>
                <span className="text-slate-500">Tax history</span><span data-testid="tax-email-preview-history">{preview.tax_history_requirement?.label} <span className="text-slate-400">(client MLC Tax History Requirement)</span></span>
                <span className="text-slate-500">Automation</span><span>{preview.enabled ? 'ON' : 'OFF'}{preview.readiness?.issues?.length ? ` · ${preview.readiness.issues.length} configuration issue(s)` : ''}</span>
              </div>
              {preview.messages.map((m) => (
                <div key={m.jurisdiction_name} className="border border-slate-200 rounded-md">
                  <div className="px-3 py-2 border-b border-slate-100 text-xs">
                    <div><span className="text-slate-500">To </span>{m.email ? <span className="font-mono">{m.email}</span> : <em className="text-rose-600">no Tax Collector email configured for {m.jurisdiction_name}</em>}</div>
                    <div><span className="text-slate-500">Subject </span><span className="font-medium" data-testid="tax-email-preview-subject">{m.subject}</span></div>
                  </div>
                  <pre className="px-3 py-3 whitespace-pre-wrap font-sans text-[13px] leading-relaxed text-slate-800" data-testid="tax-email-preview-body">{m.text}</pre>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!confirm} onOpenChange={(o) => !o && setConfirm(null)}>
        <AlertDialogContent data-testid="tax-email-resend-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Send Tax information request to {confirm?.jurisdiction_name}?</AlertDialogTitle>
            <AlertDialogDescription>
              A new standardized request email will be sent to the Tax Collector email currently configured for this
              jurisdiction{confirm?.status === 'sent' ? ' — they have already received one' : ''}. This creates a new outbound record.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="tax-email-resend-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => confirm && resend(confirm)} data-testid="tax-email-resend-confirm-btn">Send email</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
